import type { FollowUp, ListingFollowUpSequence, RealEstateCase } from "./types";

export type ListingWorkflowActionId = Extract<
  NonNullable<FollowUp["actionId"]>,
  "advertising_report" | "market_review" | "market_review_recurring"
>;

export type ListingWorkflowRule = {
  actionId: ListingWorkflowActionId;
  delayDays: number;
  purpose: string;
  completionLabel: string;
  completionTitle: string;
  nextActionId: ListingWorkflowActionId;
};

export const LISTING_FOLLOW_UP_ONE_PURPOSE = "Poslať štatistiku inzercie";
export const LISTING_FOLLOW_UP_TWO_PURPOSE = "Vyhodnotiť reakciu trhu a kontaktovať klienta";
export const LISTING_RECURRING_REVIEW_PURPOSE = "Priebežne vyhodnotiť predaj a kontaktovať klienta";

export const LISTING_WORKFLOW_RULES: Record<ListingWorkflowActionId, ListingWorkflowRule> = {
  advertising_report: {
    actionId: "advertising_report",
    delayDays: 4,
    purpose: LISTING_FOLLOW_UP_ONE_PURPOSE,
    completionLabel: "Report odoslaný",
    completionTitle: "Report inzercie odoslaný klientovi",
    nextActionId: "market_review"
  },
  market_review: {
    actionId: "market_review",
    delayDays: 4,
    purpose: LISTING_FOLLOW_UP_TWO_PURPOSE,
    completionLabel: "Klient kontaktovaný",
    completionTitle: "Reakcia trhu vyhodnotená a klient kontaktovaný",
    nextActionId: "market_review_recurring"
  },
  market_review_recurring: {
    actionId: "market_review_recurring",
    delayDays: 7,
    purpose: LISTING_RECURRING_REVIEW_PURPOSE,
    completionLabel: "Klient kontaktovaný",
    completionTitle: "Priebežné vyhodnotenie predaja dokončené",
    nextActionId: "market_review_recurring"
  }
};

const dateAtLocalTime = (value: string | Date, days: number, time: string): FollowUp => {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + days);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return { date: `${year}-${month}-${day}`, time, purpose: "" };
};

export const listingFollowUpForAction = (
  anchor: string,
  actionId: ListingWorkflowActionId,
  preferredTime = "11:00"
): FollowUp => {
  const rule = LISTING_WORKFLOW_RULES[actionId];
  return {
    ...dateAtLocalTime(anchor, rule.delayDays, preferredTime),
    purpose: rule.purpose,
    workflowId: "listing",
    actionId
  };
};

export const listingFollowUpForStep = (listingStartedAt: string, step: 1 | 2 | 3): FollowUp =>
  listingFollowUpForAction(
    listingStartedAt,
    step === 1 ? "advertising_report" : step === 2 ? "market_review" : "market_review_recurring"
  );

export const listingActionId = (caseItem: RealEstateCase): ListingWorkflowActionId | undefined => {
  if (caseItem.followUp?.workflowId === "listing" && caseItem.followUp.actionId) {
    return caseItem.followUp.actionId as ListingWorkflowActionId;
  }

  const sequence = caseItem.listingFollowUpSequence;
  if (!sequence || sequence.status !== "Aktívna" || caseItem.stage !== "Zainzerované") return undefined;
  if (sequence.currentStep === 1 && !sequence.firstFollowUpCompletedAt) return "advertising_report";
  if (sequence.currentStep === 2 && !sequence.secondFollowUpCompletedAt) return "market_review";
  if (sequence.currentStep === 3) return "market_review_recurring";
  return undefined;
};

export const canStartListingWorkflow = (caseItem: RealEstateCase) =>
  caseItem.stage === "Aktívne" &&
  !caseItem.listingStartedAt &&
  caseItem.listingFollowUpSequence?.status !== "Aktívna";

export const startListingWorkflow = (caseItem: RealEstateCase, now: string) => {
  const activeSequence = caseItem.listingFollowUpSequence?.status === "Aktívna"
    ? caseItem.listingFollowUpSequence
    : undefined;
  const alreadyStarted = Boolean(caseItem.listingStartedAt || activeSequence);
  const listingStartedAt = caseItem.listingStartedAt ?? now;
  const sequence: ListingFollowUpSequence = activeSequence ?? {
    status: "Aktívna",
    currentStep: 1
  };
  const inferredAction = sequence.currentStep === 1
    ? "advertising_report"
    : sequence.currentStep === 2
      ? "market_review"
      : "market_review_recurring";

  return {
    listingStartedAt,
    initialListingPrice: alreadyStarted
      ? caseItem.initialListingPrice ?? (caseItem.askingPrice > 0 ? caseItem.askingPrice : undefined)
      : caseItem.askingPrice > 0 ? caseItem.askingPrice : undefined,
    listingFollowUpSequence: sequence,
    followUp: alreadyStarted && caseItem.followUp ? caseItem.followUp : listingFollowUpForAction(
      sequence.currentStep === 1
        ? listingStartedAt
        : sequence.currentStep === 2
          ? sequence.firstFollowUpCompletedAt ?? listingStartedAt
          : sequence.lastMarketReviewCompletedAt ?? sequence.secondFollowUpCompletedAt ?? listingStartedAt,
      inferredAction
    ),
    startedNow: !alreadyStarted
  };
};

export const completeListingFollowUp = (caseItem: RealEstateCase, completedAt: string) => {
  const sequence = caseItem.listingFollowUpSequence;
  const currentActionId = listingActionId(caseItem);
  if (!sequence || !currentActionId || sequence.status !== "Aktívna" || caseItem.stage !== "Zainzerované") {
    return null;
  }

  const rule = LISTING_WORKFLOW_RULES[currentActionId];
  const preferredTime = caseItem.followUp?.time || "11:00";
  const followUp = listingFollowUpForAction(completedAt, rule.nextActionId, preferredTime);

  const nextSequence: ListingFollowUpSequence = currentActionId === "advertising_report"
    ? { ...sequence, currentStep: 2, firstFollowUpCompletedAt: completedAt }
    : currentActionId === "market_review"
      ? { ...sequence, currentStep: 3, secondFollowUpCompletedAt: completedAt }
      : { ...sequence, currentStep: 3, lastMarketReviewCompletedAt: completedAt };

  return {
    followUp,
    sequence: nextSequence,
    completionLabel: rule.completionLabel,
    completionTitle: rule.completionTitle
  };
};

export const recoverListingNextAction = (caseItem: RealEstateCase): FollowUp | undefined => {
  if (caseItem.stage !== "Zainzerované" || caseItem.followUp) return undefined;
  const sequence = caseItem.listingFollowUpSequence;
  if (!sequence || sequence.status === "Ukončená") return undefined;

  if (sequence.currentStep === 1 && caseItem.listingStartedAt && !sequence.firstFollowUpCompletedAt) {
    return listingFollowUpForAction(caseItem.listingStartedAt, "advertising_report");
  }
  if (sequence.currentStep === 2 && sequence.firstFollowUpCompletedAt && !sequence.secondFollowUpCompletedAt) {
    return listingFollowUpForAction(sequence.firstFollowUpCompletedAt, "market_review");
  }
  if (sequence.secondFollowUpCompletedAt) {
    return listingFollowUpForAction(
      sequence.lastMarketReviewCompletedAt ?? sequence.secondFollowUpCompletedAt,
      "market_review_recurring"
    );
  }
  return undefined;
};

export const stopListingWorkflow = (caseItem: RealEstateCase, endedAt: string, reason: string) => {
  const sequence = caseItem.listingFollowUpSequence;
  if (!sequence || sequence.status !== "Aktívna") {
    return { sequence, clearFollowUp: false };
  }

  return {
    sequence: { ...sequence, status: "Ukončená" as const, endedAt, endReason: reason },
    clearFollowUp: true
  };
};
